import { MotionConfig } from "framer-motion";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { BrandRow } from "@/lib/components/BrandRow/BrandRow";
import { MenuButton } from "@/lib/components/Menu/components/MenuButton/MenuButton";
import { MenuControlButton } from "@/lib/components/ui/MenuControlButton/MenuControlButton";
import { menuDisplayedOnPages, nonProtectedPaths } from "@/lib/config/routesConfig";
import { useMenuContext } from "@/lib/context/MenuProvider/hooks/useMenuContext";
import { useNotificationsContext } from "@/lib/context/NotificationsProvider/hooks/useNotificationsContext";
import { LogoShort } from "@/lib/helpers/customIcons";

import styles from "./Menu.module.scss";
import { NotificationsPanel } from "./components/NotificationsPanel/NotificationsPanel";
import { Threads } from "./components/Threads/Threads";
import {
  MenuActionContext,
  MenuRenderContext,menuItems,
} from "./menuConfig";
import { FeedbackModal } from "../FeedbackModal/FeedbackModal";

export const Menu = (): JSX.Element => {
  const { t } = useTranslation("translation");
  const { isOpened } = useMenuContext();
  const { isVisible, setIsVisible, unreadNotifications } =
    useNotificationsContext();
  const [isThreadsVisible, setIsThreadsVisible] = useState(true);
  const [isFeedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  if (nonProtectedPaths.some((path) => path === pathname)) {
    return <></>;
  }

  const isMenuDisplayed =
    pathname === "/" ||
    menuDisplayedOnPages.some(
      (page) => page !== "/" && pathname.includes(page),
    );

  if (!isMenuDisplayed) {
    return <></>;
  }

  const renderContext: MenuRenderContext = {
    pathname,
    isOpened,
    isNotificationsVisible: isVisible,
    unreadNotifications: unreadNotifications ?? 0,
    isThreadsVisible,
  };
  const actionContext: MenuActionContext = {
    navigate: (path) => navigate(path),
    toggleNotifications: () => setIsVisible(!isVisible),
    toggleThreads: () => setIsThreadsVisible((visible) => !visible),
  };

  return (
    <>
      <FeedbackModal
        isOpen={isFeedbackModalOpen}
        setOpen={setFeedbackModalOpen}
      />
      <MotionConfig transition={{ mass: 1, damping: 10, duration: 0.1 }}>
        <div
          className={`${styles.menu_container} ${
            isOpened ? styles.expanded : styles.compact
          }`}
        >
          <div className={styles.menu_wrapper}>
            {isOpened ? (
              <BrandRow
                className={styles.brand_wrapper}
                onClick={() => navigate("/")}
              />
            ) : (
              <div
                className={styles.brand_wrapper}
                onClick={() => navigate("/")}
              >
                <LogoShort className={styles.brand_logo_short} />
              </div>
            )}
            <div className={styles.buttons_wrapper}>
              <div className={styles.block}>
                {menuItems.map((item) => (
                  <MenuButton
                    key={item.key}
                    id={
                      item.id === "notifications"
                        ? "notifications-button"
                        : undefined
                    }
                    dataTest={item.dataTest}
                    label={isOpened ? t(item.labelKey) : undefined}
                    iconName={item.iconName}
                    color="secondary"
                    type="open"
                    isSelected={item.isSelected(renderContext)}
                    onClick={() => item.onClick(actionContext)}
                    expandable={isOpened && item.expandable}
                    expanded={item.getExpanded?.(renderContext)}
                    secondaryLabel={item.getSecondaryLabel?.(renderContext)}
                    trailingContent={item.getTrailingContent?.(renderContext)}
                  />
                ))}
              </div>
              {isOpened && isThreadsVisible && <Threads />}
            </div>
            <div className={styles.support_wrapper}>
              <MenuButton
                label={isOpened ? t("menuSupport") : undefined}
                iconName="help"
                color="secondary"
                type="open"
                onClick={() => setFeedbackModalOpen(true)}
              />
            </div>
          </div>
          <MenuControlButton />
        </div>
      </MotionConfig>
      {isVisible && (
        <div
          className={`${styles.notifications_panel} ${
            isOpened ? "" : styles.compact
          }`}
        >
          <NotificationsPanel />
        </div>
      )}
    </>
  );
};
